import DSButton from '@/components/design-system/Button';
import { Checkbox } from '@/components/design-system/SelectionControls';
import DSInput from '@/components/design-system/Input';
import React, { useMemo } from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import toast from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import { googleLoginQueryFn, signUpQueryFn } from '@/queries/auth';
import useAuth from '@/contexts/AuthContext';
import { pushToDataLayer } from '@/helpers/Analytics';
import { ErrorHandler } from '@/helpers/ErrorHandler';
import { getDevice } from '@/api/config';
import EnvelopeIcon from 'public/assets/icons/envelope.svg';
import LockIcon from 'public/assets/icons/lock.svg';
import PersonIcon from 'public/assets/icons/person.svg';
import Link from 'next/link';
import { PasswordValidator } from '@/helpers/Validators';
import i18next from 'i18n';
import { getLandingPageTrackingFromLocalStorage } from '@/helpers/LandingPageTracker';
import { AnimatePresence, motion } from 'framer-motion';
import { useGoogleLogin } from '@react-oauth/google';
import { AuthDto } from '@/api';
import DSIcon from '@/components/design-system/Icon';
import SocialLoginButton from '../SocialLoginButton';
import { useOnAccountCreated } from './useOnAccountCreated';
import { useOnAccountLogin } from './useOnAccountLogin';

export type ISignUpFormProps = {
    changeToLogin: () => void;
    isMobile?: boolean
    /**
     * Keep the visitor here after sign-up instead of sending them to
     * onboarding. Set by surfaces embedded mid-flow (e.g. a form's sign-in
     * gate), which can't afford to navigate away.
     */
    skipOnboarding?: boolean;
};

const SignUpSchema = Yup.object().shape({
    email: Yup.string().email(i18next.t('error-text.invalid-email')).required(i18next.t('error-text.required')),
    username: Yup.string().min(4, i18next.t('error-text.username-min-length')).required(i18next.t('error-text.required')),
    password: PasswordValidator,
    passwordConfirmation: Yup.string().required(i18next.t('error-text.required'))
        .oneOf([Yup.ref('password')], i18next.t('error-text.password.passwords-not-match')),
    terms: Yup.boolean().required(i18next.t('error-text.required')).oneOf([true], i18next.t('error-text.accept-terms'))
});

const SignUpForm = (props: ISignUpFormProps) => {
    const { changeToLogin, isMobile, skipOnboarding } = props;
    const { updateRefreshToken } = useAuth();

    const onAccountCreated = useOnAccountCreated(isMobile, { skipOnboarding });
    const { onAccountLogin, otpForm, showOtp } = useOnAccountLogin(isMobile);

    const onSignup = (dto: AuthDto, created: boolean) => {
        const { user: resUser, refresh_token: refreshToken } = dto;
        if (refreshToken && refreshToken !== '') {
            updateRefreshToken(refreshToken);
        }

        pushToDataLayer({ event: created ? 'signup' : 'login' });

        if (created) {
            onAccountCreated(resUser);
        } else {
            onAccountLogin(resUser);
        }

        if (created) {
            toast.success(i18next.t('success-notification.signed-up'));
        }
    };

    const signUpMutation = useMutation({
        mutationFn: signUpQueryFn,
        onSuccess: (response) => {
            if (response && response.user) {
                onSignup(response, true);
            }
        },
        onError: async (error) => {
            const exception = await ErrorHandler.transformError(error);
            if (ErrorHandler.Auth.emailExist(exception)) {
                toast.error(i18next.t('error-notification.email-taken'));
            } else if (ErrorHandler.Auth.usernameExist(exception)) {
                toast.error(i18next.t('error-notification.username-taken'));
            } else {
                toast.error(i18next.t('error-notification.unexpected-error'));
            }
        }
    });

    const googleLoginMutation = useMutation({
        mutationFn: googleLoginQueryFn,
        onSuccess: (response) => {
            if (response && response.user) {
                onSignup(response, response.created);
            }
        },
        onError: async (error) => {
            const transformedError = await ErrorHandler.transformError(error);
            if (ErrorHandler.Auth.unauthorized(transformedError)) {
                toast.error(i18next.t('error-notification.failed-google-login'));
            } else {
                toast.error(i18next.t('error-notification.unexpected-error'));
            }
        }
    });

    const googleLogin = useGoogleLogin({
        flow: 'auth-code',
        onSuccess: async (tokenResponse) => {
            googleLoginMutation.mutate({
                code: tokenResponse.code,
                device: await getDevice(),
                signed_up_from: getLandingPageTrackingFromLocalStorage()
            });
        }
    });

    const oAuthOptions = useMemo(() => {
        return (
            <motion.div key="loginoptions" className="" exit={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {/* <SocialLoginButton
                    network="apple"
                    disabled
                    onClick={() => {

                    }}
                >
                    APPLE
                </SocialLoginButton> */}
                <SocialLoginButton
                    network="google"
                    onClick={googleLogin}
                >
                    {i18next.t('general.google')}
                </SocialLoginButton>
                <div className="text-body-400 my-4 text-ink-300">{i18next.t('general.or-signup-with-email')}</div>
            </motion.div>
        );
    }, [googleLogin]);

    return (
        <div>
            <div className="text-center space-y-[24px] flex flex-col">
                <div>
                    <span className="text-h5 mb-[4px] uppercase text-ink-300">{i18next.t('general.signup')}</span>
                    <h3 className="text-hero uppercase text-ink-400">{i18next.t('general.welcome')}</h3>
                </div>
                <div>
                    <AnimatePresence mode="wait">
                        {!showOtp && oAuthOptions}
                        {!showOtp && (
                            <Formik
                                initialValues={{
                                    email: '',
                                    username: '',
                                    password: '',
                                    passwordConfirmation: '',
                                    terms: false
                                }}
                                validationSchema={SignUpSchema}
                                onSubmit={async (values) => {
                                    try {
                                        signUpMutation.mutate({
                                            body: {
                                                device: await getDevice(),
                                                email_address: values.email,
                                                password: values.password,
                                                username: values.username,
                                                signed_up_from: getLandingPageTrackingFromLocalStorage()
                                            }
                                        });
                                    } catch (error) {
                                        toast.error(i18next.t('error-notification.recapcha'));
                                    }
                                }}
                            >
                                {({
                                    values,
                                    errors,
                                    touched,
                                    handleChange,
                                    handleSubmit
                                }) => (
                                    <form onSubmit={handleSubmit}>
                                        <div className="space-y-3 relative">
                                            <div>
                                                <DSInput
                                                    autoFocus
                                                    invalid={!!(errors.email && touched.email)}
                                                    type="text"
                                                    placeholder={i18next.t('input-text.email')}
                                                    name="email"
                                                    autoComplete="username"
                                                    onChange={handleChange}
                                                    value={values.email}
                                                    icon={<EnvelopeIcon />}
                                                />
                                                {errors.email && touched.email ? (
                                                    <div className="text-body-600 text-accent-red">
                                                        {errors.email}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <div>
                                                <DSInput
                                                    autoComplete="off"
                                                    invalid={!!(errors.username && touched.username)}
                                                    type="text"
                                                    placeholder={i18next.t('input-text.username')}
                                                    name="username"
                                                    onChange={handleChange}
                                                    value={values.username}
                                                    icon={<PersonIcon />}
                                                />
                                                {errors.username && touched.username ? (
                                                    <div className="text-body-600 text-accent-red">
                                                        {errors.username}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <div>
                                                <DSInput
                                                    invalid={!!(errors.password && touched.password)}
                                                    type="password"
                                                    placeholder={i18next.t('input-text.password')}
                                                    name="password"
                                                    autoComplete="new-password"
                                                    onChange={handleChange}
                                                    value={values.password}
                                                    icon={<LockIcon />}
                                                />
                                                {errors.password && touched.password ? (
                                                    <div className="text-body-600 text-accent-red">
                                                        {errors.password}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <div>
                                                <DSInput
                                                    invalid={!!(errors.passwordConfirmation && touched.passwordConfirmation)}
                                                    type="password"
                                                    placeholder={i18next.t('input-text.confirm-password')}
                                                    name="passwordConfirmation"
                                                    autoComplete="new-password"
                                                    onChange={handleChange}
                                                    value={values.passwordConfirmation}
                                                    icon={<LockIcon />}
                                                />
                                                {errors.passwordConfirmation && touched.passwordConfirmation ? (
                                                    <div className="text-body-600 text-accent-red">
                                                        {errors.passwordConfirmation}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <div>
                                                {/* DS Checkbox: its native input is sr-only and the box
                                                    is drawn from tokens, so @tailwindcss/forms cannot
                                                    paint it the #2563eb accent it gives bare checkboxes.
                                                    Its wrapper is already a <label>, which makes the old
                                                    onClick and the separate htmlFor both redundant. */}
                                                <Checkbox
                                                    name="terms"
                                                    checked={values.terms}
                                                    onChange={handleChange}
                                                    label={(
                                                        <span>
                                                            {`${i18next.t('button-text.accept-terms-conditions.pt1')} `}
                                                            <Link
                                                                href="/terms-of-service"
                                                                target="_blank"
                                                                className="underline"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                {i18next.t('button-text.accept-terms-conditions.pt2')}
                                                            </Link>
                                                            .
                                                        </span>
                                                    )}
                                                />
                                                {errors.terms && touched.terms ? (
                                                    <div className="text-body-600 text-accent-red">
                                                        {errors.terms}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <DSButton
                                                icon={<DSIcon name="arrow-right" size={20} />}
                                                fullWidth
                                                loading={signUpMutation.isPending}
                                                disabled={signUpMutation.isPending}
                                                type="submit">
                                                {i18next.t('signup', { ns: 'header' })}
                                            </DSButton>

                                            <div />
                                        </div>
                                    </form>
                                )}
                            </Formik>
                        )}
                        {showOtp &&
                            <motion.div key="otpForm" className="space-y-[12px]" exit={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                {otpForm}
                            </motion.div>
                        }
                    </AnimatePresence>
                </div>
                {!showOtp && (
                    <div className="text-center">
                        <span className="text-body-400 block">{`${i18next.t('general.already-signed-up')} `}<button onClick={() => changeToLogin()} className="text-ink-400 underline">{i18next.t('general.login')}</button></span>
                        <span className="text-body-600 block pt-[24px] text-ink-300">{`${i18next.t('disclaimer.signup-form.pt1')} `}<Link href="/privacy-policy" target="_blank">{i18next.t('common:disclaimer.signup-form.pt2')}</Link>.</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SignUpForm;
